/// <reference types="google.maps" />

/**
 * Structured address derived from a Google Places `Place` result. Every
 * granular field is optional because partial matches (a city, a region,
 * a postal code) only populate a subset.
 */
export interface IParsedAddress {
  /** Google's pre-formatted single-line representation, e.g. "Los Angeles, CA, USA". */
  formattedAddress: string;
  /** Stable Place ID — opaque, useful for de-duping and re-fetching. */
  placeId: string;
  streetNumber?: string;
  streetName?: string;
  /** Combined unit / suite / floor for addresses that have them. */
  subpremise?: string;
  /** City / town / village. */
  city?: string;
  /** Two-letter region code, e.g. "CA". */
  state?: string;
  /** Full region name, e.g. "California". */
  stateLong?: string;
  postalCode?: string;
  /** ISO 3166-1 alpha-2 country code, e.g. "US". */
  country?: string;
  countryLong?: string;
  latitude?: number;
  longitude?: number;
}

interface IPlaceLike {
  id?: string | null;
  formattedAddress?: string | null;
  addressComponents?: google.maps.places.AddressComponent[] | null;
  location?: google.maps.LatLng | null;
}

const findComponent = (
  components: google.maps.places.AddressComponent[],
  ...types: string[]
): google.maps.places.AddressComponent | undefined => {
  for (const type of types) {
    const match = components.find((c) => c.types.includes(type));
    if (match) {
      return match;
    }
  }
  return undefined;
};

/**
 * Convert a Google Places `Place` (after `fetchFields`) into a flat,
 * easy-to-consume address shape. Pass anything `Place`-like — only the
 * fields actually present are read, so partial results are fine.
 */
export const parseAddress = (place: IPlaceLike): IParsedAddress => {
  const components = place.addressComponents ?? [];

  const streetNumber = findComponent(components, 'street_number');
  const route = findComponent(components, 'route');
  const subpremise = findComponent(components, 'subpremise');
  const city = findComponent(components, 'locality', 'postal_town', 'sublocality', 'sublocality_level_1');
  const state = findComponent(components, 'administrative_area_level_1');
  const postalCode = findComponent(components, 'postal_code');
  const country = findComponent(components, 'country');

  return {
    formattedAddress: place.formattedAddress ?? '',
    placeId: place.id ?? '',
    streetNumber: streetNumber?.longText ?? undefined,
    streetName: route?.longText ?? undefined,
    subpremise: subpremise?.longText ?? undefined,
    city: city?.longText ?? undefined,
    state: state?.shortText ?? undefined,
    stateLong: state?.longText ?? undefined,
    postalCode: postalCode?.longText ?? undefined,
    country: country?.shortText ?? undefined,
    countryLong: country?.longText ?? undefined,
    latitude: place.location?.lat(),
    longitude: place.location?.lng(),
  };
};
